<script lang="ts" setup>
import { computed, ref, type PropType } from 'vue';
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  TransitionRoot,
} from '@headlessui/vue';

const props = defineProps({
  modelValue: {
    type: [String, Number, Array],
    default: '',
  },

  options: {
    type: Array as PropType<Record<string, any>[]>,
    default: () => [],
  },

  valueKey: {
    type: String,
    default: 'value',
  },

  labelKey: {
    type: String,
    default: 'label',
  },

  pathMode: {
    type: Boolean,
    default: false,
  },

  placeholder: {
    type: String,
    default: '',
  },

  notFoundText: {
    type: String,
    default: 'No results found',
  },

  emptyText: {
    type: String,
    default: 'No options',
  },
});

const emit = defineEmits(['update:modelValue', 'change']);
const query = ref('');

const flattenArray = (arr: Record<string, any>[], level = 0, parentValues: Record<string, any>[] = []): Record<string, any>[] => {
  return arr.reduce((acc: Record<string, any>[], cur) => {
    const { children, ...rest } = cur;
    const newParentValues = [...parentValues, rest[props.valueKey]];
    const flatChildren: Record<string, any>[] = children ? flattenArray(children, level + 1, newParentValues) : [];

    return [
      ...acc,
      {
        ...rest,
        $level: level,
        $values: newParentValues,
      },
      ...flatChildren,
    ];
  }, []);
}

const flattenOptions = computed(() => flattenArray(props.options ?? []));

const arraysEqual = (a: Record<string, any>[], b: Record<string, any>[]) => {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((val, index) => val === b[index]);
};

const comboboxValue = computed({
  get() {
    if (props.pathMode) {
      return flattenOptions.value.find((item) => arraysEqual(item.$values, props.modelValue as Record<string, any>[])) ?? null;
    }
    else {
      return flattenOptions.value.find((item) => item[props.valueKey] === props.modelValue) ?? null;
    }
  },

  set(value) {
    if (props.pathMode) {
      emit('update:modelValue', [...value?.$values ?? []]);
    }
    else {
      emit('update:modelValue', value?.[props.valueKey] ?? '');
    }

    emit('change', value);
  },
});

const filteredOptions = computed(() =>
  query.value === ''
    ? flattenOptions.value
    : flattenOptions.value.filter((item) =>
      item[props.labelKey]
        .toLowerCase()
        .replace(/\s+/g, '')
        .includes(query.value.toLowerCase().replace(/\s+/g, '')),
    ),
);
</script>

<template>
  <Combobox v-model="comboboxValue">
    <div class="component-combobox relative">
      <div class="gray-border box-border h-40px flex justify-between">
        <ComboboxInput
          :placeholder="props.placeholder"
          class="w-full flex-grow rounded-4px border-none pl-16px focus:outline-none"
          :display-value="(item: any) => item?.[props.labelKey] ?? ''"
          autocomplete="off"
          @change="query = $event.target.value"
        />
        <ComboboxButton class="h-full flex cursor-pointer items-center border-none bg-transparent p-0 text-hex-2d2d2d" v-slot="{ open }">
          <div class="i-home-icons:arrow-down mx-16px size-16px transition-transform" :class="{ 'rotate-180': open }"></div>
        </ComboboxButton>
      </div>

      <TransitionRoot
        leave="transition ease-in duration-100"
        leave-from="opacity-100"
        leave-to="opacity-0"
        @after-leave="query = ''"
      >
        <ComboboxOptions class="gray-border absolute z-1 mb-0 mt-4px box-border max-h-500px w-full list-none overflow-auto bg-white px-0 py-8px">

          <div class="select-none px-16px" v-if="filteredOptions.length === 0 && query !== ''">
            {{ props.notFoundText }}
          </div>

          <div class="select-none px-16px" v-if="filteredOptions.length === 0 && query === ''">
            {{ props.emptyText }}
          </div>
          <ComboboxOption as="template" v-for="item in filteredOptions" :key="item[props.valueKey]" :value="item" v-slot="{ selected, active }" v-else>
            <li
              class="cursor-pointer select-none px-16px leading-40px"
              :class="{
                'text-brand-primary': active || selected,
                'text-hex-2d2d2d': !active && !selected,
              }"
            >
              <div
                :class="{
                  'font-medium': selected,
                  'font-normal': !selected,
                }"
              >
                <slot name="label" :data="item" :active="active" :selected="selected" v-if="$slots.label"></slot>
                <template v-else>
                  <span :style="{ 'padding-left': `${item.$level}em`, }">{{ item[props.labelKey] }}</span>
                </template>
              </div>
            </li>
          </ComboboxOption>
        </ComboboxOptions>
      </TransitionRoot>
    </div>
  </Combobox>
</template>

<style lang="scss" scoped>
.component-combobox {
  .gray-border {
    border: 1px solid rgba(154, 158, 166, 0.2);
    border-radius: 4px;
    outline-style: none;
  }
}
</style>
